import { memo, useMemo, useState, useEffect, forwardRef, useCallback } from 'react';

import Stack from '@mui/material/Stack';
import Box, { BoxProps } from '@mui/material/Box';

import ErpText from './erp-text';
import KioskSpeechBubble from './speech-bubble';

// ----------------------------------------------------------------------

export interface LogoProps extends BoxProps {
  disabledLink?: boolean;
  collapsed?: boolean;
  hideErpText?: boolean;
}

const Logo = forwardRef<HTMLDivElement, LogoProps>(
  ({ disabledLink = false, collapsed = false, hideErpText = false, sx, ...other }, ref) => {
    const [showErp, setShowErp] = useState(false);

    useEffect(() => {
      if (collapsed && showErp) {
        setShowErp(false);
      }
    }, [collapsed, showErp]);

    const handleLogoClick = useCallback(() => {
      window.location.href = '/';
    }, []);

    const logo = useMemo(
      () => (
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: 'center',
          }}
        >
          <Box
            ref={ref}
            component="img"
            src="/logo/eureka-logo.svg"
            alt="logo"
            sx={{
              width: 40,
              height: 40,
              display: 'inline-flex',
              cursor: 'pointer',
              ...sx,
            }}
            onClick={handleLogoClick}
            {...other}
          />
          {!collapsed && !hideErpText && (
            <ErpText
              showErp={showErp}
              onLogoClick={handleLogoClick}
              onToggleErp={(e: { preventDefault: () => void; stopPropagation: () => void }) => {
                e.preventDefault();
                e.stopPropagation();
                setShowErp((prev) => !prev);
              }}
            />
          )}
        </Stack>
      ),
      [ref, sx, handleLogoClick, other, collapsed, showErp, hideErpText]
    );

    const erpContent = useMemo(
      () => !hideErpText && !collapsed && <KioskSpeechBubble show={showErp} onClose={() => setShowErp(false)} />,
      [showErp, hideErpText, collapsed]
    );

    if (disabledLink) {
      return (
        <Stack sx={{ position: 'relative' }}>
          {logo}
          {erpContent}
        </Stack>
      );
    }

    return (
      <Stack sx={{ position: 'relative' }}>
        {logo}
        {erpContent}
      </Stack>
    );
  }
);

export default memo(Logo);
